interface AriSettings {
    imgProviderUrl: string;
    configId: string;
    preLoadScrollAmount: number;
    onStart?: () => void;
    onEnd?: () => void;
}

interface AriParams {
    id: string;
    hash: string;
    name: string;
    alt?: string;
    title?: string;
    maxwidth: number;
    originalwidth: string;
    originalheight: string;
    cropHash: string;
}

interface RangeSettings {
    start: number;
    end: number;
    interval: number;
}

interface AriConfig {
    imageWidthRanges: Record<string, RangeSettings>;
    minImageWidth: number;
    maxImageWidth: number;
    webPMode?: string;
}

const DEFAULTS: AriSettings = {
    imgProviderUrl: '/imgprovider/{identifier}/{hash}/{width}/{filename}',
    configId: '#internezzoAriConfig',
    preLoadScrollAmount: 300,
};

function getRatio(): number {
    return Math.min(Math.max(window.devicePixelRatio, 1), 4);
}

function getConfig(selector: string): AriConfig | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    try {
        return JSON.parse(el.innerHTML) as AriConfig;
    } catch {
        return null;
    }
}

function getParams(el: HTMLElement): AriParams | null {
    let raw: string | undefined = el.dataset.ariParams;
    if (!raw) return null;
    try {
        return JSON.parse(raw) as AriParams;
    } catch {
        raw = raw.replace(/'/g, '"').replace(/&apos;/g, "'");
        try {
            return JSON.parse(raw) as AriParams;
        } catch {
            return null;
        }
    }
}

function isInViewport(el: HTMLElement, preLoadScrollAmount: number): boolean {
    const rect = el.getBoundingClientRect();
    const clientWidth = window.innerWidth || document.documentElement.clientWidth;
    const clientHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 &&
        rect.right > 0 &&
        rect.left < clientWidth &&
        rect.top - preLoadScrollAmount < clientHeight;
}

function isToSmall(el: HTMLElement): boolean {
    const img = el.querySelector<HTMLImageElement>('img');
    if (!img) return false;
    return (img.naturalWidth / getRatio()) < el.clientWidth;
}

function getRangeSettings(config: AriConfig, imgWidth: number): RangeSettings | null {
    for (const [, rangeSettings] of Object.entries(config.imageWidthRanges)) {
        if (imgWidth >= rangeSettings.start && imgWidth <= rangeSettings.end) {
            return rangeSettings;
        }
    }
    return null;
}

function getRangedSize(
    imgWidth: number,
    imgHeight: number,
    eleRatio: number,
    respectheight: boolean,
    config: AriConfig | null,
): [number, number] {
    if (!config) return [imgWidth, imgHeight];

    let newImgWidth: number;
    const matchingRange = getRangeSettings(config, imgWidth);

    if (!matchingRange) {
        newImgWidth = imgWidth < config.minImageWidth ? config.minImageWidth : config.maxImageWidth;
    } else {
        newImgWidth = matchingRange.end;
        while (newImgWidth > imgWidth) {
            newImgWidth -= matchingRange.interval;
        }
        if (newImgWidth < imgWidth) {
            newImgWidth += matchingRange.interval;
        }
    }

    let newImgHeight = Math.round(newImgWidth * eleRatio);

    if (newImgWidth < config.minImageWidth) {
        if (respectheight) {
            newImgHeight = (config.minImageWidth * newImgHeight) / newImgWidth;
        }
        newImgWidth = config.minImageWidth;
    }

    if (newImgWidth > config.maxImageWidth) {
        if (respectheight) {
            newImgHeight = (config.maxImageWidth * newImgHeight) / newImgWidth;
        }
        newImgWidth = config.maxImageWidth;
    }

    return [Math.ceil(newImgWidth), Math.round(newImgHeight)];
}

function useWebp(config: AriConfig | null): boolean {
    if (!config || !Object.prototype.hasOwnProperty.call(config, 'webPMode')) {
        return false;
    }
    return config.webPMode === 'always';
}

function buildImageUrl(el: HTMLElement, settings: AriSettings): string | null {
    const params = getParams(el);
    if (!params) return null;

    const config = getConfig(settings.configId);
    const deviceRatio = getRatio();
    const eleRatio = el.offsetHeight / el.clientWidth || 0;
    const visibleHeight = Math.ceil(el.offsetHeight);
    const sizeFactorRaw = el.dataset.ariSizefactor;
    const respectheight = el.dataset.ariRespectheight !== undefined;
    const maxheightRaw = el.dataset.ariMaxheight;

    let imgWidth = Math.ceil(el.clientWidth * deviceRatio);
    let imgHeight = Math.ceil(el.offsetHeight * deviceRatio);

    if (sizeFactorRaw !== undefined) {
        const sizeFactor = parseFloat(sizeFactorRaw);
        if (!isNaN(sizeFactor)) {
            imgWidth = Math.ceil(imgWidth * sizeFactor);
            imgHeight = Math.ceil(imgHeight * sizeFactor);
        }
    }

    let processing = 'og';
    if (maxheightRaw !== undefined) {
        const maxheight = parseInt(maxheightRaw, 10);
        if (maxheight > 0) {
            processing = 'mh-' + maxheight;
        }
    }

    [imgWidth, imgHeight] = getRangedSize(imgWidth, imgHeight, eleRatio, respectheight, config);

    if (imgWidth > params.maxwidth) {
        imgWidth = params.maxwidth;
        imgHeight = Math.ceil(imgWidth * eleRatio);
    }

    if (!respectheight) {
        imgHeight = 0;
    }

    const filename = config && useWebp(config) ? params.name + '.webp' : params.name;

    return settings.imgProviderUrl
        .replace('{identifier}', params.id)
        .replace('{hash}', params.hash)
        .replace('{width}', imgWidth.toString())
        .replace('{height}', imgHeight.toString())
        .replace('{visibleHeight}', visibleHeight.toString())
        .replace('{filename}', filename)
        .replace('{originalwidth}', params.originalwidth)
        .replace('{originalheight}', params.originalheight)
        .replace('{processing}', processing)
        .replace('{cropHash}', params.cropHash);
}

function finishItem(el: HTMLElement): void {
    const params = getParams(el);
    const reachedSize = isToSmall(el);

    el.dataset.ariLoaded = 'true';
    el.dataset.ariSizeReached = String(reachedSize);

    const placeholder = el.querySelector<HTMLImageElement>('img:nth-child(2)');
    if (placeholder) {
        placeholder.style.transition = 'opacity 200ms';
        placeholder.style.opacity = '0';
        placeholder.addEventListener('transitionend', () => placeholder.remove());
        setTimeout(() => {
            if (placeholder.parentElement) placeholder.remove();
        }, 250);
    }

    if (reachedSize) {
        el.removeAttribute('style');
        el.classList.remove('img-ari--responsive');
        processElement(el);
    }

    if (params) {
        const img = el.querySelector<HTMLImageElement>('img');
        if (img) {
            img.alt = params.alt ?? '';
            if (params.title) img.title = params.title;
        }
    }
}

function processElement(el: HTMLElement, settings?: AriSettings): void {
    const ariSettings = settings || DEFAULTS;

    if (el.dataset.ariLoading === 'true') return;

    const isLoaded = el.dataset.ariLoaded === 'true';
    const forceLoad = el.dataset.ariForceload === 'true';
    const inView = isInViewport(el, ariSettings.preLoadScrollAmount);

    if (!((inView || forceLoad) && (!isLoaded || (isToSmall(el) && el.dataset.ariSizeReached !== 'true')))) {
        return;
    }

    if (ariSettings.onStart) ariSettings.onStart();

    el.dataset.ariLoading = 'true';

    const imgUrl = buildImageUrl(el, ariSettings);
    if (!imgUrl) {
        el.dataset.ariLoading = 'false';
        if (ariSettings.onEnd) ariSettings.onEnd();
        return;
    }

    const img = new Image();

    img.onload = function () {
        el.dataset.ariNatwidth = String(img.width);
        el.insertBefore(img, el.firstChild);
        finishItem(el);
        el.dataset.ariLoading = 'false';
        if (ariSettings.onEnd) ariSettings.onEnd();
    };

    img.onerror = function () {
        const params = getParams(el);
        const text = params?.alt || params?.title || '';
        if (text) {
            el.innerHTML = '<p>' + text + '</p>';
        }
        el.dataset.ariLoaded = 'true';
        el.dataset.ariSizeReached = 'true';
        el.removeAttribute('style');
        el.classList.remove('img-ari--responsive');
        if (ariSettings.onEnd) ariSettings.onEnd();
    };

    img.src = imgUrl;
}

export function initAri(options?: Partial<AriSettings>): void {
    const settings = { ...DEFAULTS, ...options };

    document.querySelectorAll<HTMLElement>('[data-ari-params]').forEach((el) => {
        if (el.dataset.ariSizeReached === 'true' || el.dataset.ariLoading === 'true') return;
        processElement(el, settings);
    });
}
